import type { BaseColumn, BookingColumnKey } from './BookingSheet.types';

/** Every field on a BookingRow, in the order it should appear when visible. */
// Column order as the booking sheet is read: identifiers, then flight dates,
// then who the booking is for, then the commercials, then targeting and
// ownership. Internal NRVE ids sit at the end -- available, rarely wanted.
export const COLUMN_ORDER: BookingColumnKey[] = [
  'won_date',
  'deal_id',
  'line_item_id',
  'start_date',
  'end_date',
  'buying_point',
  'advertiser',
  'campaign',
  'channel',
  'platform',
  'product',
  'format',
  'buy_model',
  'quantity',
  'currency',
  'rate',
  'gross',
  'media_spend',
  'fee',
  'discount',
  'net',
  'targeting_kpi',
  'targeting_details',
  'po',
  'am',
  'trader',
  'data_provider',
  'audience_id',
  'audience_strategy_id',
  'country',
  'nrve_media_plan_id',
  'nrve_line_item_id',
  'io_document_id'
];

/** Commercially meaningful columns shown by default; everything else is behind the column toggle. */
// Every business column is shown by default; the toggle is for narrowing down,
// not for discovering that a column exists.
export const DEFAULT_VISIBLE_COLUMNS: BookingColumnKey[] = [
  'won_date',
  'deal_id',
  'line_item_id',
  'start_date',
  'end_date',
  'buying_point',
  'advertiser',
  'campaign',
  'channel',
  'platform',
  'product',
  'format',
  'buy_model',
  'quantity',
  'currency',
  'rate',
  'gross',
  'media_spend',
  'fee',
  'net',
  'targeting_kpi',
  'targeting_details',
  'po',
  'am',
  'trader',
  'data_provider',
  'audience_id',
  'audience_strategy_id',
  'country'
];

export const COLUMN_DEFS: Record<BookingColumnKey, BaseColumn> = {
  won_date: {
    i18nKey: 'wonDate',
    dataIndex: 'won_date',
    key: 'won_date',
    width: 120,
    sorter: true,
    defaultVisible: true
  },
  deal_id: {
    i18nKey: 'dealId',
    dataIndex: 'deal_id',
    key: 'deal_id',
    width: 100,
    sorter: true,
    defaultVisible: true
  },
  line_item_id: {
    i18nKey: 'lineItemId',
    dataIndex: 'line_item_id',
    key: 'line_item_id',
    width: 90,
    sorter: true,
    defaultVisible: false
  },
  advertiser: {
    i18nKey: 'advertiser',
    dataIndex: 'advertiser',
    key: 'advertiser',
    width: 180,
    ellipsis: true,
    sorter: true,
    defaultVisible: true
  },
  campaign: {
    i18nKey: 'campaign',
    dataIndex: 'campaign',
    key: 'campaign',
    width: 200,
    ellipsis: true,
    sorter: true,
    defaultVisible: true
  },
  channel: {
    i18nKey: 'channel',
    dataIndex: 'channel',
    key: 'channel',
    width: 140,
    sorter: true,
    defaultVisible: true
  },
  platform: {
    i18nKey: 'platform',
    dataIndex: 'platform',
    key: 'platform',
    width: 140,
    sorter: true,
    defaultVisible: false
  },
  product: {
    i18nKey: 'product',
    dataIndex: 'product',
    key: 'product',
    width: 150,
    sorter: true,
    defaultVisible: true
  },
  format: {
    i18nKey: 'format',
    dataIndex: 'format',
    key: 'format',
    width: 130,
    sorter: true,
    defaultVisible: false
  },
  buy_model: {
    i18nKey: 'buyModel',
    dataIndex: 'buy_model',
    key: 'buy_model',
    width: 120,
    sorter: true,
    defaultVisible: true
  },
  quantity: {
    i18nKey: 'quantity',
    dataIndex: 'quantity',
    key: 'quantity',
    width: 110,
    align: 'right',
    className: 'numeric-cell',
    sorter: true,
    defaultVisible: true
  },
  currency: {
    i18nKey: 'currency',
    dataIndex: 'currency',
    key: 'currency',
    width: 90,
    sorter: true,
    defaultVisible: false
  },
  rate: {
    i18nKey: 'rate',
    dataIndex: 'rate',
    key: 'rate',
    width: 110,
    align: 'right',
    className: 'numeric-cell',
    sorter: true,
    defaultVisible: true
  },
  gross: {
    i18nKey: 'gross',
    dataIndex: 'gross',
    key: 'gross',
    width: 130,
    align: 'right',
    className: 'numeric-cell',
    sorter: true,
    defaultVisible: true
  },
  media_spend: {
    i18nKey: 'mediaSpend',
    dataIndex: 'media_spend',
    key: 'media_spend',
    width: 130,
    align: 'right',
    className: 'numeric-cell',
    sorter: true,
    defaultVisible: false
  },
  fee: {
    i18nKey: 'fee',
    dataIndex: 'fee',
    key: 'fee',
    width: 110,
    align: 'right',
    className: 'numeric-cell',
    sorter: true,
    defaultVisible: false
  },
  discount: {
    i18nKey: 'discount',
    dataIndex: 'discount',
    key: 'discount',
    width: 120,
    align: 'right',
    className: 'numeric-cell',
    sorter: true,
    defaultVisible: true
  },
  net: {
    i18nKey: 'net',
    dataIndex: 'net',
    key: 'net',
    width: 130,
    align: 'right',
    className: 'numeric-cell',
    sorter: true,
    defaultVisible: true
  },
  targeting_kpi: {
    i18nKey: 'targetingKpi',
    dataIndex: 'targeting_kpi',
    key: 'targeting_kpi',
    width: 140,
    sorter: true,
    defaultVisible: false
  },
  targeting_details: {
    i18nKey: 'targetingDetails',
    dataIndex: 'targeting_details',
    key: 'targeting_details',
    width: 180,
    ellipsis: true,
    sorter: true,
    defaultVisible: false
  },
  po: {
    i18nKey: 'po',
    dataIndex: 'po',
    key: 'po',
    width: 130,
    sorter: true,
    defaultVisible: true
  },
  am: {
    i18nKey: 'am',
    dataIndex: 'am',
    key: 'am',
    width: 140,
    sorter: true,
    defaultVisible: false
  },
  trader: {
    i18nKey: 'trader',
    dataIndex: 'trader',
    key: 'trader',
    width: 140,
    sorter: true,
    defaultVisible: false
  },
  data_provider: {
    i18nKey: 'dataProvider',
    dataIndex: 'data_provider',
    key: 'data_provider',
    width: 150,
    sorter: true,
    defaultVisible: false
  },
  audience_id: {
    i18nKey: 'audienceId',
    dataIndex: 'audience_id',
    key: 'audience_id',
    width: 140,
    sorter: true,
    defaultVisible: false
  },
  audience_strategy_id: {
    i18nKey: 'audienceStrategyId',
    dataIndex: 'audience_strategy_id',
    key: 'audience_strategy_id',
    width: 160,
    sorter: true,
    defaultVisible: false
  },
  country: {
    i18nKey: 'country',
    dataIndex: 'country',
    key: 'country',
    width: 110,
    sorter: true,
    defaultVisible: false
  },
  start_date: {
    i18nKey: 'startDate',
    dataIndex: 'start_date',
    key: 'start_date',
    width: 120,
    // Sortable: traders plan by when a line goes live, not when it was won.
    sorter: true,
    defaultVisible: true
  },
  end_date: {
    i18nKey: 'endDate',
    dataIndex: 'end_date',
    key: 'end_date',
    width: 120,
    sorter: true,
    defaultVisible: true
  },
  buying_point: {
    i18nKey: 'buyingPoint',
    dataIndex: 'buying_point',
    key: 'buying_point',
    width: 140,
    sorter: true,
    defaultVisible: false
  },
  io_document_id: {
    i18nKey: 'ioDocumentId',
    dataIndex: 'io_document_id',
    key: 'io_document_id',
    width: 160,
    ellipsis: true,
    defaultVisible: false
  },
  nrve_media_plan_id: {
    i18nKey: 'mediaPlanId',
    dataIndex: 'nrve_media_plan_id',
    key: 'nrve_media_plan_id',
    width: 160,
    ellipsis: true,
    defaultVisible: false
  },
  nrve_line_item_id: {
    i18nKey: 'lineItemId',
    dataIndex: 'nrve_line_item_id',
    key: 'nrve_line_item_id',
    width: 160,
    ellipsis: true,
    defaultVisible: false
  }
};
